import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../../store'
import { apiClient } from '../../base_url/config'
import {
  emptyPromotionEvaluation,
  promotionEvaluationFromResponse,
  type PromotionEvaluation,
} from './promotionEvaluation'

interface OffersState {
  evaluation: PromotionEvaluation
  isLoading: boolean
  lastEvalKey: string | null
  requestId: number
}

const initialState: OffersState = {
  evaluation: emptyPromotionEvaluation,
  isLoading: false,
  lastEvalKey: null,
  requestId: 0,
}

function fingerprintFromCart(apiCartData: RootState['cart']['apiCartData']): string {
  if (!apiCartData) return ''
  const parts = [
    String(apiCartData.id ?? ''),
    String(apiCartData.total_price ?? ''),
    String((apiCartData as { price?: string | number }).price ?? ''),
  ]
  for (const item of apiCartData.items ?? []) {
    parts.push(`${item.id ?? ''}:${item.quantity ?? ''}`)
  }
  return parts.join('|')
}

/**
 * Evaluates the current cart offer via POST /qr/promotions/evaluate.
 * Payment method is fixed to "online" for QR checkout.
 */
export const evaluateCartOffer = createAsyncThunk(
  'offers/evaluateCartOffer',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const state = getState() as RootState
    const apiCartData = state.cart.apiCartData
    const cartId = apiCartData?.id != null ? Number(apiCartData.id) : null

    if (cartId == null || Number.isNaN(cartId) || cartId <= 0) {
      dispatch(clearOffer())
      return emptyPromotionEvaluation
    }

    const paymentMethod = 'online'
    const fingerprint = fingerprintFromCart(apiCartData)
    const requestKey = `${cartId}|${paymentMethod}|${fingerprint}`
    if (requestKey === state.offers.lastEvalKey) {
      return state.offers.evaluation
    }

    const requestId = state.offers.requestId + 1
    dispatch(beginOfferEvaluation({ requestKey, requestId }))

    try {
      const response = await apiClient.post('/qr/promotions/evaluate', {
        cartId,
        paymentMethod,
      })

      const current = getState() as RootState
      if (requestId !== current.offers.requestId) {
        return rejectWithValue('stale')
      }

      return promotionEvaluationFromResponse(response.data)
    } catch (error) {
      const current = getState() as RootState
      if (requestId !== current.offers.requestId) {
        return rejectWithValue('stale')
      }
      if (process.env.NODE_ENV === 'development') {
        console.warn('Promotion evaluate error:', error)
      }
      return emptyPromotionEvaluation
    }
  },
)

const offersSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    beginOfferEvaluation: (
      state,
      action: PayloadAction<{ requestKey: string; requestId: number }>,
    ) => {
      state.lastEvalKey = action.payload.requestKey
      state.requestId = action.payload.requestId
      state.isLoading = true
      state.evaluation = emptyPromotionEvaluation
    },
    clearOffer: (state) => {
      state.lastEvalKey = null
      state.requestId += 1
      state.isLoading = false
      state.evaluation = emptyPromotionEvaluation
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(evaluateCartOffer.fulfilled, (state, action) => {
        state.evaluation = action.payload ?? emptyPromotionEvaluation
        state.isLoading = false
      })
      .addCase(evaluateCartOffer.rejected, (state, action) => {
        if (action.payload === 'stale') return
        state.evaluation = emptyPromotionEvaluation
        state.isLoading = false
      })
  },
})

export const { beginOfferEvaluation, clearOffer } = offersSlice.actions

export const selectOfferEvaluation = (state: RootState) => state.offers.evaluation
export const selectIsOfferLoading = (state: RootState) => state.offers.isLoading

export default offersSlice.reducer
